import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    MenuItem,
    Chip,
    TextField,
    InputAdornment,
    TablePagination,
} from "@mui/material";
import Paper from '@mui/material/Paper';
import { CiSearch } from "react-icons/ci";
import { FaPlus } from "react-icons/fa6";
import { useLanguage } from "../../../context/LanguageContext/LanguageContext";
import { GoDotFill } from "react-icons/go";
import './units.scss'
import { useNavigate } from "react-router-dom";
import FilterButton from "../FilterDropdownButton";
import { useDispatch, useSelector } from "react-redux";
import { fetchEquipments } from "../../../redux/slices/myAccount/equipmentsSlice";
import { FaStar } from "react-icons/fa";
import NotFound from "../../NotFoundService/NotFoundService";
import CustomPagination from "../Pagination/Pagination";
import Loading from "../../UI/Loading/Loading";
const Units = () => {
    const { translations, language, direction } = useLanguage();
    const t = translations;
    const { isLoggedIn, user, token } = useSelector(state => state.login);
    const { equipments, loading, error } = useSelector(state => state.equipments);

    const [searchValue, setSearchValue] = useState("");
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [selectedFilter, setSelectedFilter] = useState("All"); // Default filter
    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    useEffect(() => {
        // Calculate pagination values
        const draw = page + 1;  // Typically, draw starts from 1 (not 0)
        const start = page * rowsPerPage;  // The starting record index for pagination
        const length = rowsPerPage;  // The number of records per page

        // Determine the status based on selectedFilter
        let validStatus;
        if (selectedFilter === "All") {
            validStatus = null; // All statuses (null)
        } else if (selectedFilter === "Pending") {
            validStatus = 1; // Status 1 for pending
        } else if (selectedFilter === "Accepted") {
            validStatus = 2; // Status 2 for accepted
        } else if (selectedFilter === "Rejected") {
            validStatus = 3; // Status 3 for rejected
        }
        const customerFk = user?.id || 0; // Default to 0 if no user ID available
        // Determine the search and text values based on searchValue
        const search = searchValue
            ? { value: searchValue, regex: true } // If searchValue exists, set regex to true
            : { value: null, regex: false }; // If no searchValue, set null and regex to false
        const text = searchValue || null; // Set text to searchValue or null if not present

        // Create the params object with all the values
        const params = {
            draw: page + 1,                      // Draw value (pagination offset + 1)
            start: page * rowsPerPage,           // Start index (pagination offset)
            length: rowsPerPage,                 // Number of records per page
            search: search,                      // Search object (if search exists)
            isWeb: true,                         // Web request flag
            customerFk: user?.id || 0,           // Customer FK (user ID or 0)
            text: searchValue || null,           // Search text (or null if no search value)
            status: validStatus,                 // Status (based on selectedFilter)
        };
        // Dispatch the fetchEquipments action with the params
        dispatch(fetchEquipments({ params, token, language }))
            .then(response => {
                // console.log("Fetch Equipments Response: ", response.payload); // Log the response from the thunk
            })
            .catch(error => {
                console.error("Error fetching equipments: ", error); // Log the error if the request fails
            });
    }, [dispatch, selectedFilter, searchValue, user.id, page, rowsPerPage]);
    // Assuming this is the filtered units logic in your parent component
    const filteredUnits = Array.isArray(equipments.message.data)
        ? equipments.message.data.filter((unit) => {
            if (selectedFilter === "All") return true; // Show all units if "All" is selected

            // Map the selectedFilter to the correct status code (1, 2, 3, or null)
            let statusFilter;
            switch (selectedFilter) {
                case "Accepted":
                    statusFilter = 2;
                    break;
                case "Pending":
                    statusFilter = 1;
                    break;
                case "Rejected":
                    statusFilter = 3;
                    break;
                default:
                    statusFilter = null;
            }

            // Filter units based on status
            return unit.status === statusFilter;
        })
        : [];

    const paginatedUnits = filteredUnits.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to first page
    };
    const handleNewUnitClick = () => {
        navigate('/my-account/add-unit');
    };
    const handleFilterChange = (filter) => {
        setSelectedFilter(filter);
    };
    if (loading) return <Loading/>;
    if (!isLoggedIn) {
        navigate('/signin');
        return null;
    }
    if (!equipments || equipments.length === 0) {
        return <NotFound message="No Units data available." />;
    }
    return (
        <Box sx={{ padding: { md: 3, xs: "24px 0px 24px 0px" } }}>
            {/* Search and Actions */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 2,
                    flexDirection: { lg: "row", md: "column", xs: "column" },
                    mt: { md: "0px", xs: "30px" },
                    gap: "20px"
                }}
            >
                {/* Search Input */}
                <TextField
                    variant="outlined"
                    placeholder={t.searchPlaceholder}
                    InputProps={{
                        disableUnderline: true, // Remove the border
                        endAdornment: (
                            <InputAdornment position="end">
                                <CiSearch style={{ fontSize: '28px', color: '#A4A4A4' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        width: '300px',
                        height: '56px',
                        backgroundColor: '#FDFDFD',
                        boxShadow: '0px 11px 23px 0px #00000005',
                        borderRadius: '12px',
                        '& .MuiOutlinedInput-root': {
                            border: 'none',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '& .MuiInputBase-input::placeholder': {
                            fontSize: '16px',
                        },
                    }}
                    onChange={(e) => setSearchValue(e.target.value)}
                />

                {/* Action Buttons */}
                <Box sx={{ display: "flex", gap: "24px", }}>
                    <Button
                        onClick={handleNewUnitClick}
                        variant="contained"
                        sx={{
                            backgroundColor: "var(--primary-color)",
                            textTransform: "none",
                            fontSize: { md: "16px", xs: "13px" },
                            fontWeight: 600,
                            padding: { md: "10px 20px", xs: "12px" },
                            "&:hover": {
                                backgroundColor: "var(--primary-color-dark)",
                            },
                            borderRadius: "12px",
                        }}
                    >
                        {t.newUnitButton} <FaPlus className="plus-icon" style={{ color: "#fff" }} />
                    </Button>
                    <FilterButton
                        selectedFilter={selectedFilter}
                        onFilterChange={handleFilterChange}
                        labels={{
                            acceptedLabel: t.acceptedLabel,
                            rejectedLabel: t.rejectedLabel,
                            pendingLabel: t.pendingLabel,
                            allLabel: t.allLabel
                        }}
                        direction={direction}
                    />


                </Box>
            </Box>
            <Box sx={{ width: "100%" }}>
                <TableContainer
                    component={Paper}
                    sx={{
                        maxWidth: {
                            xs: "auto",        // Default for extra small screens
                            sm: "auto",        // Default for small screens
                            md: "600px",       // For medium screens (900px and above)
                            lg: "100%",        // For large screens
                        },
                        overflowX: "auto",  // Enable horizontal scrolling
                        "@media (min-width: 900px) and (max-width: 1004px)": { // Custom range for 900px to 1004px screens
                            maxWidth: "490px", // Set maxWidth to 590px for this range
                        },
                    }}
                >
                    <Table >
                        <TableHead>
                            <TableRow>
                                <TableCell ><strong>{t.unitName}</strong></TableCell>
                                <TableCell ><strong>{t.dateCheckIn}</strong></TableCell>
                                <TableCell ><strong>{t.description2}</strong></TableCell>
                                <TableCell ><strong>{t.project}</strong></TableCell>
                                <TableCell ><strong>{t.building}</strong></TableCell>
                                <TableCell><strong>{t.floor}</strong></TableCell>
                                <TableCell ><strong>{t.status}</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                        {paginatedUnits.length > 0 ? (
                            paginatedUnits.map((unit, index) => (
                                <TableRow key={index}>
                                    <TableCell sx={{ fontSize: "14px", fontWeight: 400, color: "#3E4954", padding: "20px" }}>
                                        <Box sx={{ position: "relative" }}>

                                            {/* Equipment Name */}
                                            {unit.equipmentName}
                                            {unit.isNewUnit && (
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        top: "-10px", // Adjusts the label position above the name
                                                        left: "40%",
                                                        transform: "translateX(-50%)",
                                                        fontSize: "12px", // Make the label smaller
                                                        color: "#FFD700",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        fontWeight: 700
                                                    }}
                                                >
                                                    <FaStar />

                                                </Box>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{wordBreak: 'break-word' , fontSize: "14px", fontWeight: 400, color: "#3E4954", padding: "20px" }}>
                                        {new Date(unit.dateCheckIn).toLocaleDateString()} {/* Format date as needed */}

                                    </TableCell>
                                    <TableCell sx={{ fontSize: "14px", fontWeight: 400, color: "#3E4954", padding: "20px",wordBreak: 'break-word'  }}>
                                        {unit.equipmentDescription}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "14px", fontWeight: 400, color: "#3E4954", padding: "20px",wordBreak: 'break-word'  }}>
                                        {unit.project}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "14px", fontWeight: 400, color: "#3E4954", padding: "20px",wordBreak: 'break-word'  }}>
                                        {unit.building}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "14px", fontWeight: 400, color: "#3E4954", padding: "20px",wordBreak: 'break-word'  }}>
                                        {unit.floorNumber}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "14px", fontWeight: 400 }}>

                                        <Chip
                                            label={
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <GoDotFill
                                                        style={{
                                                            fontSize: "10px",
                                                            color:
                                                                unit.status === 2
                                                                    ? "#209F84"
                                                                    : unit.status === 1
                                                                        ? "#FF5C00"
                                                                        : "#FF2C53", // Assuming status 1 is pending and 2 is accepted
                                                            marginRight: "8px",
                                                        }}
                                                    />
                                                    {unit.status === 2
                                                        ? t.accepted
                                                        : unit.status === 1
                                                            ? t.pending
                                                            : t.rejected}
                                                </Box>
                                            }
                                            sx={{
                                                backgroundColor:
                                                    unit.status === 2
                                                        ? "#209F841A"
                                                        : unit.status === 1
                                                            ? "#FF5C0026"
                                                            : "#F3E9E8",
                                                color:
                                                    unit.status === 2
                                                        ? "#209F84"
                                                        : unit.status === 1
                                                            ? "#FF5C00"
                                                            : "#FF2C53",
                                                fontWeight: "bold",
                                                borderRadius: "8px",
                                                padding: "8px 12px",
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center" style={{ color: "#A4A4A4", fontWeight: "bold" }}>
                                   {t.noData}
                                </TableCell>
                            </TableRow>
                        )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <CustomPagination
                page={page}
                setPage={setPage}
                totalPages={Math.ceil(filteredUnits.length / rowsPerPage)}
                totalData={filteredUnits.length}
                itemsPerPage={rowsPerPage}
            />


            {/* <TablePagination
                rowsPerPageOptions={[5, 10, 15]}
                component="div"
                count={filteredUnits.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            /> */}


        </Box>
    );
};

export default Units;
